import { Link } from 'react-router-dom'
import {
  getGameModeByKey,
  getFootballGameMode,
  useHomeGameModes,
  type FootballGameMode,
  type GameModeParticipation,
} from '@/features/game-modes/api'
import { getGreetingName, useCurrentUser } from '@/features/user/api'
import { useTranslation } from '@/i18n'

export function GameModesPage() {
  const { t } = useTranslation()
  const currentUserQuery = useCurrentUser(true)
  const gameModesQuery = useHomeGameModes()
  const football = getFootballGameMode(gameModesQuery.data)
  const manager = getGameModeByKey(gameModesQuery.data, 'fantasy')
  const trivia = getGameModeByKey(gameModesQuery.data, 'quiz_one_vs_one')
  const greetingName = getGreetingName(currentUserQuery.data)

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-black tracking-tight text-white sm:text-2xl">
            {t('Hoi {name}', { name: greetingName })}
          </h1>
          <span className="text-xl leading-none" aria-hidden="true">👋</span>
        </div>
        <p className="mt-1.5 text-sm text-surface-400 sm:text-base">
          {t('Leuk dat je Golero gebruikt. Doe mee aan onze voetbalpoules of word manager in Golero Manager, onze fantasy league.')}
        </p>
      </div>

      {gameModesQuery.isLoading ? <FootballCardSkeleton /> : null}

      {gameModesQuery.isError ? (
        <StateCard
          title={t('Home niet geladen')}
          body={t('De spelmodi konden niet worden opgehaald. Probeer het opnieuw.')}
          action={t('Opnieuw proberen')}
          onAction={() => void gameModesQuery.refetch()}
        />
      ) : null}

      {!gameModesQuery.isLoading && !gameModesQuery.isError ? (
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <div>
            {football ? (
              <FootballPouleCard game={football} />
            ) : (
              <StateCard
                title={t('Voetbalpoules zijn tijdelijk niet beschikbaar')}
                body={t('Probeer het later opnieuw of open een van de andere spelmodi.')}
              />
            )}
          </div>

          <div className="space-y-4">
            {manager ? (
              <GameModeCard
                to="/dashboard/manager"
                badge={t('Manager')}
                badgeClassName="bg-cyan-500/15 text-cyan-300"
                title={manager.name || t('Golero Manager')}
                subtitle={t('Stel je basiself op, kies je formatie en coach je team vanaf de zijlijn.')}
                cta={manager.participation?.is_joined ? t('Openen') : t('Ontdek')}
              />
            ) : null}

            {trivia ? (
              <GameModeCard
                to="/dashboard/trivia"
                badge={t('Trivia')}
                badgeClassName="bg-amber-500/15 text-amber-300"
                title={trivia.name || t('Golero Trivia')}
                subtitle={t('Test je kennis en daag je vrienden uit.')}
                cta={t('Openen')}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function FootballPouleCard({ game }: { game: FootballGameMode }) {
  const { t } = useTranslation()
  const summary = game.bootstrap?.poule_summary
  const live = game.bootstrap?.live_matches_summary
  const participation = game.participation as GameModeParticipation | undefined
  const isJoined = participation?.is_joined === true
  const joinedCount = summary?.joined_poules_count ?? participation?.joined_count ?? 0
  const toPredictCount = summary?.to_predict_count ?? 0
  const liveCount = live?.matches_count ?? 0

  return (
    <section className="relative overflow-hidden rounded-[1.5rem] border border-white/[0.06] bg-[#0d1113] shadow-[0_20px_60px_rgba(0,0,0,0.2)]">
      <div className="relative p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <span className="game-mode-pill inline-flex rounded-full bg-[#171a1c] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-surface-300">
            {t('Voetbalpoules')}
          </span>
          {isJoined ? (
            <span className="game-mode-status-pill rounded-full border border-emerald-300/20 bg-emerald-300/10 px-2.5 py-1 text-[10px] font-semibold text-emerald-200">
              {t('Actief')}
            </span>
          ) : null}
        </div>

        <h1 className="mt-5 max-w-xl text-2xl font-black tracking-tight text-white sm:text-3xl">
          {t('Golero Voetbalpoules')}
        </h1>
        <p className="mt-2 max-w-xl text-sm leading-6 text-surface-300 sm:text-base">
          {t('Voorspel wedstrijden, verdien punten en klim samen met je vrienden in de ranking.')}
        </p>

        <div className="game-mode-stats mt-5 grid grid-cols-3 gap-2 rounded-[1rem] border border-white/[0.08] bg-black/20 p-2">
          <StatTile label={t('Mijn poules')} value={String(joinedCount)} />
          <StatTile label={t('Te voorspellen')} value={String(toPredictCount)} />
          <StatTile label={t('Live')} value={String(liveCount)} isLive={live?.has_live_matches === true} />
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <Link
            to={isJoined ? '/dashboard/poules?open=predictions' : '/dashboard/poules'}
            className="group inline-flex min-h-12 items-center justify-start gap-2 rounded-xl bg-accent-600 px-4 py-3 text-left text-sm font-bold text-white transition hover:bg-accent-500"
          >
            <span className="min-w-0 flex-1 text-left">{isJoined ? t('Snel voorspellen') : t('Ontdek poules')}</span>
            <span className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true">→</span>
          </Link>
          <Link
            to="/dashboard/poules"
            className="group inline-flex min-h-12 items-center justify-start gap-2 rounded-xl border border-white/[0.12] bg-[#151a1c] px-4 py-3 text-left text-sm font-bold text-white transition hover:bg-white/[0.09]"
          >
            <span className="min-w-0 flex-1 text-left">{t('Mijn poule-overzicht')}</span>
            <span className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  )
}

function StatTile({
  label,
  value,
  isLive = false,
}: {
  label: string
  value: string
  isLive?: boolean
}) {
  return (
    <div className="min-w-0 rounded-[0.75rem] px-2 py-2.5 text-center sm:px-3 sm:text-left">
      <p className="truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-500">
        {label}
      </p>
      <p className={['mt-1 text-xl font-black', isLive ? 'text-emerald-300' : 'text-white'].join(' ')}>
        {isLive ? <span className="mr-1.5 inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-300 align-middle" /> : null}
        {value}
      </p>
    </div>
  )
}

function FootballCardSkeleton() {
  return (
    <div className="h-[25rem] animate-pulse rounded-[1.5rem] border border-white/[0.06] bg-[#151a1c] p-5 sm:p-6">
      <div className="h-6 w-28 rounded-full bg-[#1b2020]" />
      <div className="mt-8 h-9 max-w-sm rounded-lg bg-[#1b2020]" />
      <div className="mt-3 h-5 max-w-lg rounded-lg bg-[#171a1c]" />
      <div className="mt-8 h-20 rounded-2xl bg-[#171a1c]" />
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <div className="h-12 rounded-xl bg-[#1b2020]" />
        <div className="h-12 rounded-xl bg-[#171a1c]" />
      </div>
    </div>
  )
}

function StateCard({
  title,
  body,
  action,
  onAction,
}: {
  title: string
  body: string
  action?: string
  onAction?: () => void
}) {
  return (
    <section className="rounded-[1.5rem] border border-white/[0.06] bg-[#111416] p-5 sm:p-6">
      <h1 className="text-xl font-black tracking-tight text-white">{title}</h1>
      <p className="mt-2 max-w-xl text-sm leading-6 text-surface-400">{body}</p>
      {action && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="mt-4 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500"
        >
          {action}
        </button>
      ) : null}
    </section>
  )
}

function GameModeCard({
  to,
  badge,
  badgeClassName,
  title,
  subtitle,
  cta,
}: {
  to: string
  badge: string
  badgeClassName: string
  title: string
  subtitle: string
  cta: string
}) {
  return (
    <Link
      to={to}
      className="block rounded-[1.5rem] border border-white/[0.06] bg-[#0d1113] p-5 transition hover:bg-[#171a1c] sm:p-6"
    >
      <span
        className={`game-mode-pill inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${badgeClassName}`}
      >
        {badge}
      </span>
      <h2 className="mt-3 text-xl font-black tracking-tight text-white sm:text-2xl">{title}</h2>
      <p className="mt-2 max-w-xl text-sm leading-6 text-surface-400">{subtitle}</p>
      <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white">
        {cta}
        <span aria-hidden="true">→</span>
      </span>
    </Link>
  )
}
